/**
 * ANDROID TAP TO PAY — create one from the POS Terminals tab.
 *
 * The SAME RubyTill Pay lane (`dejavoo_rubytill_pay` → `credit_dejavoo_rtpay`,
 * emvMode `rtpay`, pairing code, relay) on a plain Android phone/tablet. The only
 * difference is the card engine on the device: Dejavoo's Tap to Pay SDK (NFC,
 * credit only) instead of DvPayLite, which registers with the TPN plus a
 * Merchant Code. Spec: docs/agenticpos-rubytill/rubytill-pay.md
 *
 *   1. Register the device       POST …/emv-devices  {paymentConfigId:'dejavoo_rubytill_pay', rtpayCardEngine:'tap_to_pay', …}
 *   2. Bind it to a register     PUT  …/payment-types (optional; same contract as the Payment Types tab)
 *   3. Pair it                   RubyTillPayDevicePanel, pairing code open at once
 */
import React, { useMemo, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SearchableDropdown from '../SearchableDropdown';
import { RubyGemMark } from '../RubyGemMark';
import { RubyTillPayDevicePanel } from './RubyTillPayDevicePanel';
import {
  RtpayModeCards, ANDROID_TAP_TO_PAY_MODEL, RTPAY_CLASS, isValidMerchantCode, isValidTpn,
  type RtpayRegisterMode,
} from './RubyTillPayRegisterFields';
import { assignTender, buildRegisterPatch, registerFromResponses, registerMayExist } from './payment-types/model';

const RTPAY_TENDER = 'credit_dejavoo_rtpay';

export interface TapToPayLocation { locationId: string; name: string }
export interface TapToPayRegister {
  terminalId: string;
  registerName: string;
  locationId: string;
  terminalType?: string;
  status?: string | null;
  registerStatus?: string | null;
}

/** The registers a Tap to Pay device may be bound to: real tills at that location. */
export function bindableRegisters(terminals: TapToPayRegister[], locationId: string): TapToPayRegister[] {
  return terminals.filter((t) => t.locationId === locationId && t.terminalType !== 'online_sales' && registerMayExist(t));
}

/** The register POST body — exported for tests. */
export function tapToPayRegisterBody(f: { deviceName: string; tpn: string; merchantCode: string; mode: RtpayRegisterMode }) {
  return {
    deviceName: f.deviceName.trim() || ANDROID_TAP_TO_PAY_MODEL,
    model: ANDROID_TAP_TO_PAY_MODEL,
    paymentConfigId: RTPAY_CLASS,
    rtpayCardEngine: 'tap_to_pay' as const,
    rtpayMerchantCode: f.merchantCode.trim(),
    spinTpn: f.tpn.trim(),
    rtpayMode: f.mode,
  };
}

/**
 * Bind a RubyTill Pay device as the register's card reader — the Payment Types
 * tab's own model (assignTender → buildRegisterPatch → putPaymentTypes), so
 * Credit swaps to `credit_dejavoo_rtpay` and nothing else on the register moves.
 * Refuses to write when the register's current payment types cannot be read:
 * a patch built from an empty list would drop cash.
 */
export async function bindRtpayToRegister(tenantId: string, registerId: string, deviceId: string): Promise<void> {
  const [pt, emv] = await Promise.all([
    api.posNmi.getPaymentTypes(tenantId, registerId).catch(() => null),
    api.posNmi.getEmvDevice(tenantId, registerId).catch(() => null),
  ]);
  if (!pt || !Array.isArray(pt.assigned)) throw new Error('Could not read this register’s payment types.');
  const base = registerFromResponses(pt, null, emv?.paired ? (emv.device?.deviceId || null) : null);
  const patch = buildRegisterPatch(base, assignTender(base, RTPAY_TENDER, deviceId));
  if (patch) await api.posNmi.putPaymentTypes(tenantId, registerId, patch as any);
}

const INPUT = 'min-h-[44px] w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm text-stone-800 outline-none focus:border-ruby focus:ring-2 focus:ring-rose-200';

interface Props {
  tenantId: string;
  locations: TapToPayLocation[];
  locationsLoading: boolean;
  terminals: TapToPayRegister[];
  onClose: () => void;
  /** After the device registers (and binds) — the caller refreshes its lists. */
  onCreated?: () => void;
}

export const AndroidTapToPayCreate: React.FC<Props> = ({ tenantId, locations, locationsLoading, terminals, onClose, onCreated }) => {
  const [locationId, setLocationId] = useState('');
  const [deviceName, setDeviceName] = useState(ANDROID_TAP_TO_PAY_MODEL);
  const [tpn, setTpn] = useState('');
  const [merchantCode, setMerchantCode] = useState('');
  const [mode, setMode] = useState<RtpayRegisterMode>('charge_only');
  const [registerId, setRegisterId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ deviceId: string; deviceName: string; boundTo: string | null } | null>(null);

  const registers = useMemo(() => bindableRegisters(terminals, locationId), [terminals, locationId]);
  const tpnBad = !!tpn && !isValidTpn(tpn);
  const mcBad = !!merchantCode && !isValidMerchantCode(merchantCode);
  const canSubmit = !!locationId && isValidTpn(tpn) && isValidMerchantCode(merchantCode) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      const resp = await api.posNmi.registerEmvDeviceToLocation(
        tenantId, locationId, tapToPayRegisterBody({ deviceName, tpn, merchantCode, mode }),
      );
      const newId: string | undefined = resp?.device?.deviceId || resp?.deviceId;
      if (!newId) throw new Error('The device registered but no id came back — find it in the location’s payment devices.');
      let boundTo: string | null = null;
      if (registerId) {
        const reg = registers.find((r) => r.terminalId === registerId);
        try {
          await bindRtpayToRegister(tenantId, registerId, newId);
          boundTo = reg?.registerName || registerId;
        } catch (e: any) {
          toastService.warning(`Registered, but not bound to ${reg?.registerName || 'the register'}: ${e?.serverMessage || e?.message || 'the service refused'}. Bind it on that register’s Payment Types.`);
        }
      }
      setCreated({ deviceId: newId, deviceName: resp?.device?.deviceName || deviceName.trim() || ANDROID_TAP_TO_PAY_MODEL, boundTo });
      toastService.success('Android Tap to Pay registered — pair it on the device');
      onCreated?.();
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'Could not register the device.');
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5 space-y-3" data-testid="ttp-created">
        <div>
          <h3 className="text-stone-800 font-semibold text-sm">Pair {created.deviceName}</h3>
          <p className="text-stone-500 text-xs mt-1">
            Open RubyTill Pay on the Android device and type the code below.
            {created.boundTo ? ` It takes cards for ${created.boundTo}.` : ' Bind it to a register on that register’s Payment Types.'}
          </p>
        </div>
        <div className="-mx-5 overflow-hidden rounded-xl">
          <RubyTillPayDevicePanel
            tenantId={tenantId}
            device={{ deviceId: created.deviceId, deviceName: created.deviceName, model: ANDROID_TAP_TO_PAY_MODEL, rtpayCardEngine: 'tap_to_pay' }}
            startPairing
          />
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="min-h-[44px] rounded-lg bg-stone-100 px-4 text-xs font-medium text-stone-600 hover:bg-stone-200">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5 space-y-4" data-testid="ttp-form">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-stone-900 text-white"><Smartphone size={16} /></span>
        <div>
          <h3 className="text-stone-800 font-semibold text-sm">Add Android Tap to Pay</h3>
          <p className="text-stone-500 text-xs mt-1">
            An Android phone or tablet that takes contactless cards (credit, tap only) with RubyTill Pay. It pairs with a code — no SPIn setup.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-stone-500 text-xs mb-1 block">Location *</label>
          {locationsLoading ? (
            <div className="flex items-center gap-2 text-stone-400 text-sm py-2"><RubyGemMark size={14} drawing /> Loading locations...</div>
          ) : locations.length === 0 ? (
            <p className="text-amber-600 text-xs py-2">No active locations found for this tenant.</p>
          ) : (
            <SearchableDropdown
              options={locations.map((l) => ({ value: l.locationId, label: l.name }))}
              value={locationId || null}
              onChange={(v) => { setLocationId(v || ''); setRegisterId(''); }}
              placeholder="— Select Location —"
              ariaLabel="Location"
              searchPlaceholder="Search locations..."
            />
          )}
        </div>
        <div>
          <label className="text-stone-500 text-xs mb-1 block">Device name</label>
          <input value={deviceName} onChange={(e) => setDeviceName(e.target.value)} maxLength={60}
            placeholder={ANDROID_TAP_TO_PAY_MODEL} className={INPUT} data-testid="ttp-name" />
        </div>
        <div>
          <label className="text-stone-500 text-xs mb-1 block">TPN * <span className="text-stone-400">(12 digits)</span></label>
          <input value={tpn} inputMode="numeric" onChange={(e) => setTpn(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="000000000000" className={`${INPUT} font-mono ${tpnBad ? 'border-red-300 bg-red-50' : ''}`} data-testid="ttp-tpn" />
        </div>
        <div>
          <label className="text-stone-500 text-xs mb-1 block">Merchant Code * <span className="text-stone-400">(digits)</span></label>
          <input value={merchantCode} inputMode="numeric" autoComplete="off"
            onChange={(e) => setMerchantCode(e.target.value.replace(/\D/g, '').slice(0, 20))}
            placeholder="From Dejavoo, for this TPN" className={`${INPUT} font-mono ${mcBad ? 'border-red-300 bg-red-50' : ''}`} data-testid="ttp-merchant-code" />
          {mcBad && <p className="mt-1 text-[11px] text-red-700">Digits only, 6 to 20 long.</p>}
        </div>
        <div className="sm:col-span-2">
          <label className="text-stone-500 text-xs mb-1 block">What it does at the register</label>
          <RtpayModeCards value={mode} onChange={setMode} disabled={submitting} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-stone-500 text-xs mb-1 block">Register <span className="text-stone-400">(optional — binds it as that till’s card reader)</span></label>
          {!locationId ? (
            <p className="text-stone-400 text-xs py-2">Pick a location first.</p>
          ) : registers.length === 0 ? (
            <p className="text-stone-400 text-xs py-2">No registers at this location yet — bind it later on a register’s Payment Types.</p>
          ) : (
            <SearchableDropdown
              options={registers.map((r) => ({ value: r.terminalId, label: r.registerName || r.terminalId }))}
              value={registerId || null}
              onChange={(v) => setRegisterId(v || '')}
              placeholder="Not now"
              ariaLabel="Register"
              searchPlaceholder="Search registers..."
            />
          )}
        </div>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="ttp-error">{error}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="min-h-[44px] rounded-lg bg-stone-100 px-4 text-xs font-medium text-stone-600 hover:bg-stone-200">
          Cancel
        </button>
        <button onClick={submit} disabled={!canSubmit} data-testid="ttp-submit"
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
          {submitting ? <><RubyGemMark size={14} drawing color="currentColor" /> Registering...</> : 'Register & get pairing code'}
        </button>
      </div>
    </div>
  );
};

export default AndroidTapToPayCreate;
